/**
 * deviceRevoke.ts
 *
 * Shared utility for removing a person from physical devices.
 *
 * Key exports:
 *  - fetchPersonDevices       — fetch DeviceInfo[] for a person's zones
 *  - removePersonFromDevices  — remove from a given list, parallel, with retries
 *  - removePersonFromAllDevices — fetch + remove (call BEFORE backend revoke)
 *  - revokeEntryDevices       — remove from entrada-only devices after check-in;
 *                               persists failures to localStorage for retry
 *  - retryPendingEntryRevokes — retry loop (call every minute from hook)
 *  - revokeExitDevices        — remove from ALL devices after check-out/manual
 *                               revoke (no direction filter); persists failures
 *                               to localStorage for retry
 *  - retryPendingExitRevokes  — retry loop (call every minute from hook)
 *  - retryPendingDeviceRevokes — runs both entry + exit retry loops together
 */

import md5 from 'md5';
import { fetchWithAuth } from '../services/api';
import {
  DeviceOperations,
  CommunicationMode,
  DeviceInfo,
  OperationSource,
} from '../services/deviceCommunication';
import { AdapterFactory } from '../services/adapters';
import { DeviceIntegration, rawDeviceToHandle } from '../integrations/devices';
import { logger } from '../services/logger';
import { mayStillHoldCredential } from './credentialPresence';
import { announceDeviceCredentialChange } from '../services/deviceCredentialAnnounce';

// ─── localStorage persistence ─────────────────────────────────────────────────

const ENTRY_STORAGE_KEY = 'panther:pendingEntryRevokes';
const EXIT_STORAGE_KEY = 'panther:pendingExitRevokes';

// A person stuck failing on several devices would otherwise be fully re-retried
// (one credential-outcome POST per still-failing device) on every single 60s
// retry tick, forever. After a few free retries (covers ordinary transient
// failures), back off exponentially per person instead, capped at MAX_BACKOFF_MS.
// Never gives up permanently — it just stops hammering the network.
const RETRY_INTERVAL_MS = 60_000;
const FREE_RETRIES = 3;
const MAX_BACKOFF_MS = 30 * 60_000; // 30 minutes

function nextBackoffMs(attempts: number): number {
  if (attempts <= FREE_RETRIES) return 0;
  return Math.min(RETRY_INTERVAL_MS * 2 ** (attempts - FREE_RETRIES), MAX_BACKOFF_MS);
}

interface PendingRevoke {
  customerId: string;
  personId: string;
  label: string;
  devices: DeviceInfo[];       // only the ones that still need removal
  savedAt: string;             // ISO — for debugging / TTL pruning
  attempts?: number;           // retry-cycle attempts so far (undefined/0 = never retried)
  nextAttemptAt?: string;      // ISO — skip retrying this item until this time
}

function makeStorage(key: string) {
  function loadPending(): PendingRevoke[] {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }

  function savePending(items: PendingRevoke[]): void {
    try { localStorage.setItem(key, JSON.stringify(items)); } catch {}
  }

  function upsertPending(item: PendingRevoke): void {
    const items = loadPending().filter(r => r.personId !== item.personId);
    items.push(item);
    savePending(items);
  }

  function clearPending(personId: string): void {
    savePending(loadPending().filter(r => r.personId !== personId));
  }

  return { loadPending, savePending, upsertPending, clearPending };
}

const entryStorage = makeStorage(ENTRY_STORAGE_KEY);
const exitStorage = makeStorage(EXIT_STORAGE_KEY);

// ─── Low-level single-device attempt ─────────────────────────────────────────

interface RemoveResult {
  success: boolean;
  latencyMs: number;
  source: OperationSource;
  errorMessage?: string;
  readbackVerified?: boolean;
}

async function tryRemoveFromDevice(
  device: DeviceInfo,
  personId: string,
  maxRetries: number,
): Promise<RemoveResult> {
  let lastError: string | undefined;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await DeviceIntegration.instance.execute(
        device, DeviceOperations.REMOVE_PERSON, { person_id: personId }, CommunicationMode.HYBRID,
      );
      if (res.success) {
        // ControlID's REMOVE_PERSON path (removeControlIDPerson) now does a real
        // load_objects.fcgi read-back after destroy_objects.fcgi and sets this field —
        // other vendors/paths that don't independently verify leave it undefined.
        return { success: true, latencyMs: res.latencyMs, source: res.source, readbackVerified: res.readbackVerified };
      }
      lastError = res.error || 'device operation returned success=false';
    } catch (err: any) {
      lastError = err?.message || String(err);
    }
    if (attempt < maxRetries) await new Promise(r => setTimeout(r, 1000 * attempt));
  }
  // Previously this silently returned {success:false} with no error detail, and the
  // caller only logged locally — condo-manager never learned a removal failed. Now
  // every failure is reported server-side via reportCredentialOutcome below.
  return { success: false, latencyMs: 0, source: 'cloud', errorMessage: lastError };
}

// ─── Verified removal (device-truth loop) ─────────────────────────────────────

export interface VerifiedRemoveResult extends RemoveResult {
  /** Person was already absent — confirmed by the device's own people list. */
  alreadyAbsent?: boolean;
}

/**
 * Asks the DEVICE ITSELF whether the person is enrolled, using the vendor's
 * targeted query (Hikvision: UserInfo/Search filtered by EmployeeNoList =
 * md5(personId), a single-result exact lookup). DIRECT mode only — presence
 * checking is a local-LAN concern; the cloud must never be asked to contact
 * the device on our behalf.
 *
 * Returns true/false when the device answered conclusively, or null when the
 * answer cannot be trusted (request failed, or the vendor's lookup cannot
 * distinguish "absent" from "unmatchable" — ControlID users created via
 * add_user.fcgi often have a null registration, and its REMOVE_PERSON path
 * already does its own load-then-delete + read-back).
 */
async function isPersonOnDevice(device: DeviceInfo, personId: string): Promise<boolean | null> {
  if (device.vendor !== 'hikvision') return null;
  try {
    const res = await DeviceIntegration.instance.execute(
      device, DeviceOperations.LOAD_PEOPLE, { person_id: personId }, CommunicationMode.DIRECT,
    );
    if (!res.success) return null;
    const people = AdapterFactory.getAdapter(device.vendor).parseLoadPeopleResponse(res.data);
    const employeeNo = md5(personId);
    return people.some(p => p.employeeNo === employeeNo);
  } catch {
    return null;
  }
}

/**
 * Remove a person from one device with EVIDENCE, not hope:
 *
 *   1. Ask the device if the person is even there — already absent is a real,
 *      verified outcome that needs no delete at all.
 *   2. Remove via the vendor path (ControlID internally read-back-verifies).
 *   3. Read back — a delete that "succeeded" but still shows the person on the
 *      device is a FAILURE, reported as such instead of being trusted.
 *
 * The readbackVerified flag on the result is what the backend records as the
 * stronger electron_readback outcome source.
 */
export async function removePersonVerified(
  device: DeviceInfo,
  personId: string,
  maxRetries: number,
): Promise<VerifiedRemoveResult> {
  const started = Date.now();

  const presentBefore = await isPersonOnDevice(device, personId);
  if (presentBefore === false) {
    logger.info('device.revoke', `Person already absent on device ${device.id} (verified by device people list)`);
    return {
      success: true,
      readbackVerified: true,
      alreadyAbsent: true,
      latencyMs: Date.now() - started,
      source: 'local',
    };
  }

  const result = await tryRemoveFromDevice(device, personId, maxRetries);
  if (!result.success) return result;

  // Vendors whose remove path doesn't verify on its own (Hikvision) get an
  // explicit read-back here.
  if (result.readbackVerified === undefined) {
    const presentAfter = await isPersonOnDevice(device, personId);
    if (presentAfter === true) {
      return {
        success: false,
        latencyMs: Date.now() - started,
        source: result.source,
        errorMessage: 'delete reported success but read-back still finds the person on the device',
      };
    }
    if (presentAfter === false) {
      return { ...result, readbackVerified: true };
    }
  }
  return result;
}

export interface DeviceRemovalOutcome {
  device: DeviceInfo;
  result: VerifiedRemoveResult;
}

/**
 * Verified variant of removePersonFromDevices: every device attempt goes through
 * removePersonVerified and every outcome (success, already-absent, failure) is
 * reported to condo-manager. Returns the per-device outcomes so callers (the
 * pendência queue drainer, the exit-detection immediate pass) can report exactly
 * what happened instead of a bare boolean.
 */
export async function removePersonFromDevicesVerified(
  customerId: string,
  devices: DeviceInfo[],
  personId: string,
  label: string,
  maxRetries = 2,
): Promise<{ allSucceeded: boolean; outcomes: DeviceRemovalOutcome[] }> {
  if (typeof window === 'undefined' || !window.electronAPI) {
    logger.info('device.revoke', 'Skipping verified removal — not Electron', { personId });
    return { allSucceeded: false, outcomes: [] };
  }
  if (devices.length === 0) return { allSucceeded: true, outcomes: [] };

  const outcomes = await Promise.all(devices.map(async (device) => {
    const result = await removePersonVerified(device, personId, maxRetries);
    if (result.success) {
      logger.info('device.revoke', `Removed "${label}" from ${device.id}`, {
        verified: result.readbackVerified === true,
        alreadyAbsent: result.alreadyAbsent === true,
      });
    } else {
      logger.error('device.revoke', `Failed to remove "${label}" from ${device.id}`, { error: result.errorMessage });
    }
    reportCredentialOutcome(customerId, personId, device.id, result);
    return { device, result };
  }));

  return { allSucceeded: outcomes.every(o => o.result.success), outcomes };
}

/**
 * Reports a REAL per-attempt outcome (success OR failure) to condo-manager, so
 * device_credential_states always reflects what actually happened — not just
 * successes. Previously, failures here were tracked ONLY in this browser's
 * localStorage (see retryPendingEntryRevokes) and were invisible to the backend;
 * if the app or session ended, that "still pending" fact was lost with no trace.
 *
 * Exported so other Electron-path call sites (e.g. the deferred add/remove queue
 * in useDeferredDeviceRemovals) report through the same funnel instead of only
 * updating local state.
 */
export function reportCredentialOutcome(
  customerId: string,
  personId: string,
  deviceId: string,
  result: RemoveResult,
  operation: 'grant' | 'revoke' = 'revoke',
): void {
  fetchWithAuth(
    `/api/v1/customers/${customerId}/people/${personId}/devices/${deviceId}/credential-outcome`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credential_type: 'face',
        operation,
        outcome: result.success
          ? (operation === 'grant' ? 'confirmed_present' : 'confirmed_absent')
          : 'failed',
        source: result.source === 'local' ? 'electron_hybrid_direct' : 'electron_hybrid_cloud_fallback',
        error_message: result.errorMessage,
        duration_ms: result.latencyMs,
        readback_verified: result.readbackVerified ?? false,
      }),
    }
  ).catch(() => {});

  // Same signal the sync funnel emits: a revoke changes what every "is this person on that
  // reader" view should be showing, including the Dispositivos de Pessoas page.
  announceDeviceCredentialChange();
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Fetch DeviceInfo for every device the person currently has access to, with zone direction. */
export async function fetchPersonDevices(customerId: string, personId: string): Promise<DeviceInfo[]> {
  try {
    const zonesResp = await fetchWithAuth(
      `/api/v1/customers/${customerId}/people/${personId}/effective-access-zones`
    );
    if (!zonesResp.ok) return [];
    const zonesData = await zonesResp.json();
    const zones: any[] = zonesData.zones || [];

    const deviceIdSet = new Set<string>();
    const directionMap = new Map<string, 'entrada' | 'saida' | 'passagem'>();

    zones.forEach((z: any) => {
      z.devices?.forEach((d: any) => {
        deviceIdSet.add(d.id);
        if (z.entry_type && !directionMap.has(d.id)) {
          directionMap.set(d.id, z.entry_type);
        }
      });
    });

    // Zones alone are not the whole truth. A person removed from a zone keeps whatever was
    // written to that zone's readers, and the device then disappears from this list — so the
    // one device that most needs clearing was the one we never tried. The ledger remembers.
    try {
      const statesResp = await fetchWithAuth(
        `/api/v1/customers/${customerId}/people/${personId}/device-credential-states`
      );
      if (statesResp.ok) {
        const statesData = await statesResp.json();
        const states: any[] = statesData.data || statesData || [];
        states.forEach((state: any) => {
          if (state?.device_id && mayStillHoldCredential(state.state)) {
            deviceIdSet.add(state.device_id);
          }
        });
      }
    } catch {
      // The ledger is an addition here, never a precondition: a revoke must still clear the
      // zone devices when this call fails.
    }

    if (deviceIdSet.size === 0) return [];

    const deviceDetails = await Promise.all(
      Array.from(deviceIdSet).map(async (deviceId): Promise<DeviceInfo | null> => {
        try {
          const res = await fetchWithAuth(`/api/v1/iot-devices/${deviceId}`);
          if (!res.ok) return null;
          const d = await res.json();
          return rawDeviceToHandle(d, directionMap.get(deviceId));
        } catch { return null; }
      })
    );

    return deviceDetails.filter((d): d is DeviceInfo => d !== null);
  } catch { return []; }
}

/**
 * Remove a person from a list of devices — all in parallel, with retries.
 * IMPORTANT: call with devices fetched BEFORE any backend revoke so zones are still populated.
 * Logs each successful removal to the audit trail via mark-revoked.
 * Returns true only if ALL devices were successfully removed.
 */
export async function removePersonFromDevices(
  customerId: string,
  devices: DeviceInfo[],
  personId: string,
  label: string,
  maxRetries = 3
): Promise<boolean> {
  if (typeof window === 'undefined' || !window.electronAPI) {
    logger.info('device.revoke', 'Skipping — not Electron', { personId });
    return false;
  }
  if (devices.length === 0) return true;

  const results = await Promise.all(devices.map(async (device) => {
    const result = await tryRemoveFromDevice(device, personId, maxRetries);
    if (result.success) {
      logger.info('device.revoke', `Removed "${label}" from ${device.id}`);
    } else {
      logger.error('device.revoke', `Failed to remove "${label}" from ${device.id} after ${maxRetries} attempts`, { error: result.errorMessage });
    }
    // Report every attempt, success or failure — this used to only report success.
    reportCredentialOutcome(customerId, personId, device.id, result);
    return result.success;
  }));

  return results.every(Boolean);
}

/** Remove from all authorized devices. Call BEFORE backend revoke. Returns true if all succeeded. */
export async function removePersonFromAllDevices(
  customerId: string, personId: string, label: string, maxRetries = 3
): Promise<boolean> {
  const devices = await fetchPersonDevices(customerId, personId);
  return removePersonFromDevices(customerId, devices, personId, label, maxRetries);
}

/**
 * Called on check-in: remove visitor from entrada devices and persist any
 * failures to localStorage so the retry routine can clear them.
 * Logs each successful removal to the audit trail via mark-revoked.
 */
export async function revokeEntryDevices(
  customerId: string,
  personId: string,
  label: string,
  allDevices: DeviceInfo[],
  maxRetries = 3
): Promise<boolean> {
  if (typeof window === 'undefined' || !window.electronAPI) return false;

  const entryDevices = allDevices.filter(d => d.direction === 'entrada');
  if (entryDevices.length === 0) return true;

  // Persist immediately — ensures retry even if the app crashes mid-removal
  entryStorage.upsertPending({ customerId, personId, label, devices: entryDevices, savedAt: new Date().toISOString() });

  const results = await Promise.all(
    entryDevices.map(async device => ({
      device,
      result: await removePersonVerified(device, personId, maxRetries),
    }))
  );

  const failed = results.filter(r => !r.result.success).map(r => r.device);

  // Report every attempt, success or failure — a failure here previously stayed
  // purely local (localStorage) until eventually retried away, with condo-manager
  // never knowing an entry-device removal had failed in the meantime.
  results.forEach(({ device, result }) => {
    reportCredentialOutcome(customerId, personId, device.id, result);
  });

  if (failed.length === 0) {
    entryStorage.clearPending(personId);
    logger.info('device.revoke', `Entry access fully revoked for "${label}"`);
    return true;
  }
  // Keep only failed devices in localStorage for retry
  entryStorage.upsertPending({ customerId, personId, label, devices: failed, savedAt: new Date().toISOString() });
  logger.warn('device.revoke', `${failed.length} entry device(s) pending retry for "${label}"`);
  return false;
}

/**
 * Retry routine — call every minute from the hook.
 * Processes all pending entry revokes in parallel; clears successes from localStorage.
 */
export async function retryPendingEntryRevokes(): Promise<void> {
  if (typeof window === 'undefined' || !window.electronAPI) return;

  const now = Date.now();
  const pending = entryStorage.loadPending();
  const due = pending.filter(item => !item.nextAttemptAt || new Date(item.nextAttemptAt).getTime() <= now);
  if (due.length === 0) return;

  logger.info('device.revoke', `Retrying ${due.length} pending entry revoke(s)`);

  await Promise.all(due.map(async (item) => {
    const results = await Promise.all(
      item.devices.map(async device => ({
        device,
        result: await tryRemoveFromDevice(device, item.personId, 1), // single attempt per retry cycle
      }))
    );

    const failed = results.filter(r => !r.result.success).map(r => r.device);

    // Report every attempt, success or failure.
    results.forEach(({ device, result }) => {
      reportCredentialOutcome(item.customerId, item.personId, device.id, result);
    });

    if (failed.length === 0) {
      entryStorage.clearPending(item.personId);
      logger.info('device.revoke', `Pending entry revoke cleared for "${item.label}"`);
    } else {
      const attempts = (item.attempts || 0) + 1;
      entryStorage.upsertPending({
        ...item,
        devices: failed,
        savedAt: new Date().toISOString(),
        attempts,
        nextAttemptAt: new Date(now + nextBackoffMs(attempts)).toISOString(),
      });
    }
  }));
}

/**
 * Called on check-out (automatic or manual): remove visitor from ALL devices
 * they had access to — unlike revokeEntryDevices, this is NOT filtered by
 * direction, since at exit the person must lose access to every device
 * (entrada- and saida-direction both). Persists failures to localStorage so
 * the retry routine can clear them, mirroring the entry-side behavior.
 * Logs each successful removal to the audit trail via mark-revoked.
 */
export async function revokeExitDevices(
  customerId: string,
  personId: string,
  label: string,
  allDevices: DeviceInfo[],
  maxRetries = 3
): Promise<boolean> {
  if (typeof window === 'undefined' || !window.electronAPI) return false;
  if (allDevices.length === 0) return true;

  // Persist immediately — ensures retry even if the app crashes mid-removal
  exitStorage.upsertPending({ customerId, personId, label, devices: allDevices, savedAt: new Date().toISOString() });

  const results = await Promise.all(
    allDevices.map(async device => ({
      device,
      result: await removePersonVerified(device, personId, maxRetries),
    }))
  );

  const failed = results.filter(r => !r.result.success).map(r => r.device);

  // Report every attempt, success or failure.
  results.forEach(({ device, result }) => {
    reportCredentialOutcome(customerId, personId, device.id, result);
  });

  if (failed.length === 0) {
    exitStorage.clearPending(personId);
    logger.info('device.revoke', `Exit access fully revoked for "${label}"`);
    return true;
  }
  // Keep only failed devices in localStorage for retry
  exitStorage.upsertPending({ customerId, personId, label, devices: failed, savedAt: new Date().toISOString() });
  logger.warn('device.revoke', `${failed.length} exit device(s) pending retry for "${label}"`);
  return false;
}

/**
 * Retry routine — call every minute from the hook.
 * Processes all pending exit revokes in parallel; clears successes from localStorage.
 */
export async function retryPendingExitRevokes(): Promise<void> {
  if (typeof window === 'undefined' || !window.electronAPI) return;

  const now = Date.now();
  const pending = exitStorage.loadPending();
  const due = pending.filter(item => !item.nextAttemptAt || new Date(item.nextAttemptAt).getTime() <= now);
  if (due.length === 0) return;

  logger.info('device.revoke', `Retrying ${due.length} pending exit revoke(s)`);

  await Promise.all(due.map(async (item) => {
    const results = await Promise.all(
      item.devices.map(async device => ({
        device,
        result: await tryRemoveFromDevice(device, item.personId, 1), // single attempt per retry cycle
      }))
    );

    const failed = results.filter(r => !r.result.success).map(r => r.device);

    // Report every attempt, success or failure.
    results.forEach(({ device, result }) => {
      reportCredentialOutcome(item.customerId, item.personId, device.id, result);
    });

    if (failed.length === 0) {
      exitStorage.clearPending(item.personId);
      logger.info('device.revoke', `Pending exit revoke cleared for "${item.label}"`);
    } else {
      const attempts = (item.attempts || 0) + 1;
      exitStorage.upsertPending({
        ...item,
        devices: failed,
        savedAt: new Date().toISOString(),
        attempts,
        nextAttemptAt: new Date(now + nextBackoffMs(attempts)).toISOString(),
      });
    }
  }));
}

/** Runs both the entry and exit retry loops together — call every minute from the hook. */
export async function retryPendingDeviceRevokes(): Promise<void> {
  await Promise.all([retryPendingEntryRevokes(), retryPendingExitRevokes()]);
}
